import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import NProgress from 'nprogress';
import { useEffect } from 'react';
import { createBrowserRouter, Outlet, useNavigation } from 'react-router';
import { AuthProvider } from './contexts/auth-context';
import DashboardLayout from './layouts/dashboard-layout';
import AnalyticsPage from './pages/analytics';
import CategoriesPage from './pages/categories';
import CommentsPage from './pages/comments';
import FeaturedPage from './pages/featured';
import IdolsPage from './pages/idols';
import LoginPage from './pages/login';
import NotFoundPage from './pages/not-found';
import ReportsPage from './pages/reports';
import SeriesPage from './pages/series';
import SettingsPage from './pages/settings';
import StudiosPage from './pages/studios';
import UsersPage from './pages/users';
import VideosPage from './pages/videos';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

// Navigation progress component
function NavigationProgress() {
  const navigation = useNavigation();

  useEffect(() => {
    if (navigation.state === 'loading') {
      NProgress.start();
    } else {
      NProgress.done();
    }
  }, [navigation.state]);

  return null;
}

// Root layout with providers
function RootLayout() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <NavigationProgress />
        <Outlet />
        <ReactQueryDevtools initialIsOpen={false} />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: '/login',
        element: <LoginPage />,
      },
      {
        path: '/',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <AnalyticsPage />,
          },
          {
            path: 'comments',
            element: <CommentsPage />,
          },
          {
            path: 'reports',
            element: <ReportsPage />,
          },
          {
            path: 'users',
            element: <UsersPage />,
          },
          {
            path: 'featured',
            element: <FeaturedPage />,
          },
          {
            path: 'settings',
            element: <SettingsPage />,
          },
          {
            path: 'videos',
            element: <VideosPage />,
          },
          {
            path: 'idols',
            element: <IdolsPage />,
          },
          {
            path: 'categories',
            element: <CategoriesPage />,
          },
          {
            path: 'studios',
            element: <StudiosPage />,
          },
          {
            path: 'series',
            element: <SeriesPage />,
          },
        ],
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);
